import { Platform } from 'react-native';
import { FirebaseFunctionsAPI } from './firebaseFunctionsAPI';

interface TranscriptionResponse {
  success: boolean;
  text?: string;
  error?: string;
}

class AudioTranscriptionAPI extends FirebaseFunctionsAPI {

  async transcribeAudio(audioUri: string): Promise<string> {
    let base64Audio: string;
    let fileExtension: string;
    
    if (Platform.OS === 'web') {
      // Web: Handle blob URLs
      const audioResponse = await fetch(audioUri);
      const audioBlob = await audioResponse.blob();
      
      // Get format from blob MIME type
      const mimeType = audioBlob.type; // e.g., 'audio/webm', 'audio/wav'
      console.log('📄 Web audio MIME type:', mimeType);
      
      if (mimeType.startsWith('audio/')) {
        fileExtension = mimeType.split('/')[1].split(';')[0]; // Extract format from MIME
      } else {
        // Default to webm for web recordings
        fileExtension = 'webm';
      }
      
      console.log('📄 Audio file format:', fileExtension, 'URI:', audioUri);
      
      base64Audio = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result as string;
          // Remove the data:audio/xxx;base64, prefix
          const base64Data = base64String.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
    } else {
      // Native: Use expo-file-system
      const FileSystem = require('expo-file-system');
      
      // Extract file extension from URI
      const uriParts = audioUri.split('.');
      fileExtension = uriParts[uriParts.length - 1].toLowerCase();
      
      // iOS often uses .caf which OpenAI doesn't support, convert to m4a
      if (fileExtension === 'caf') {
        fileExtension = 'm4a';
      }
      
      console.log('📄 Audio file format:', fileExtension, 'URI:', audioUri);
      
      // Read file as base64
      base64Audio = await FileSystem.readAsStringAsync(audioUri, {
        encoding: FileSystem.EncodingType.Base64,
      });
    }

    const response = await this.makeRequest<{
      result?: TranscriptionResponse;
    } & TranscriptionResponse>('audioToText', {
      audio: base64Audio,
      fileExtension: fileExtension, // Tell backend the audio format
      language: 'en'
    });
    
    // Callable functions called via HTTP also wrap in result
    const data = (response as any).result || response;
    
    if (!data.success || !data.text) {
      throw new Error('Transcription failed');
    }

    return data.text;
  }
}

const audioTranscriptionAPI = new AudioTranscriptionAPI();

/**
 * Transcribes audio file to text using OpenAI Whisper API
 * @param audioUri - Local URI of the audio file  
 * @returns Promise<string> - Transcribed text
 */
export async function transcribeAudio(audioUri: string): Promise<string> {
  return audioTranscriptionAPI.transcribeAudio(audioUri);
}

 